// Table view's own Food Log card — deliberately NOT the shared
// calendar/components/log-cards/LogFoodLogCard.js (that one stays untouched,
// used as-is by the athlete/coach calendar Log view). This is a Table-only
// variant: same merge/sort logic and the same `log-card`/`log-card__*`
// classNames (so it inherits the existing dark theme from calendar-log.css
// without editing that file), plus a macro-totals header row the shared
// card doesn't have — the sum of proteins/carbs/fats actually logged
// through these food-log meals (independent of the day's overall
// `nutrients` total, which may include other sources). Each meal also shows
// its non-zero P/C/F under its kcal, and each food its portion, kcal and
// non-zero P/C/F — the shared card lists bare names.
// SYNC NOTE: this duplicates LogFoodLogCard's merge/sort/meal-kcal logic on
// purpose (isolation over reuse, by explicit request). If that logic changes
// there (e.g. mergeFoodLogs' source-merging rules), check here too —
// nothing enforces the two staying in sync.
import PropTypes from "prop-types";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import SvgIcon from "../../../../../parts/SvgIcon";
import {
  getServingLabel,
  mergeFoodLogs,
} from "../../../../../utils/daily-stats";
import { mealOrderIndex } from "./viewTokens";

// Calculates the meal kcal
const getMealKcal = (meal) => {
  if (meal.kcal != null) return Math.round(meal.kcal);
  return Math.round((meal.proteins || 0) * 4 + (meal.carbs || 0) * 4 + (meal.fats || 0) * 9);
};

// Sorts the meals by name
const sortMeals = (meals) =>
  [...meals].sort((a, b) => mealOrderIndex(a.name) - mealOrderIndex(b.name));

// Sums the macros
const sumMacros = (meals) =>
  meals.reduce(
    (totals, meal) => ({
      proteins: totals.proteins + (meal.proteins || 0),
      carbs: totals.carbs + (meal.carbs || 0),
      fats: totals.fats + (meal.fats || 0),
    }),
    { proteins: 0, carbs: 0, fats: 0 }
  );

// Per-food kcal: the food's own kcal when it has one, else derived from its
// macros (same fallback getMealKcal uses for meals), else null — so a food
// with no energy data at all shows nothing rather than a made-up "0". A real
// kcal of 0 (water, black coffee) is kept.
const getFoodKcal = (food) => {
  if (food.kcal != null) return Math.round(food.kcal);
  const { proteins = 0, carbs = 0, fats = 0 } = food;
  if (!proteins && !carbs && !fats) return null;
  return Math.round(proteins * 4 + carbs * 4 + fats * 9);
};

// Macros to show for a food or a meal: always all three, in P/C/F order (same
// as the header badges), so P/C/F keep the same position line after line and a
// real 0 (e.g. a Coke's protein) is visible. The exception is an item whose
// three macros are ALL 0/missing: some integrations store a missing macro as
// 0 (e.g. the MFP parser), so "0P · 0C · 0F" there can't be told apart from
// "no data" — nothing is shown instead.
const MACRO_KEYS = ["proteins", "carbs", "fats"];
// Same colors as the header's P/C/F badges (np-table-detail-total__badge--*).
const MACRO_TEXT_CLASS = {
  proteins: "text-(--protein)",
  carbs: "text-(--carbs)",
  fats: "text-(--fat)",
};
const MACRO_ABBR_KEY = {
  proteins: "proteinAbbr",
  carbs: "carbsAbbr",
  fats: "fatAbbr",
};
const getMacros = (item) => {
  const macros = MACRO_KEYS.map((key) => ({
    key,
    value: Math.round(item[key] || 0),
  }));
  return macros.some(({ value }) => value > 0) ? macros : [];
};

// Colored macro values ("26P · 20C · 5F"), "·"-separated like the Day Drawer
// and header rows, with a `title` (macro name + grams) so the color isn't the
// only cue. Used for both a meal and each of its foods.
const MacroValues = ({ macros, t }) => (
  <span className="flex shrink-0 items-baseline gap-1 tabular-nums">
    {macros.map(({ key, value }, index) => (
      <Fragment key={key}>
        {index > 0 && <span className="text-(--white-40)">·</span>}
        <span
          className={`font-semibold ${MACRO_TEXT_CLASS[key]}`}
          title={`${t(`nutritionPage.progress.macros.${key}`)} ${value}${t("common:units.gramAbbr")}`}
        >
          {value}
          {t(`common:macros.${MACRO_ABBR_KEY[key]}`)}
        </span>
      </Fragment>
    ))}
  </span>
);

MacroValues.propTypes = {
  macros: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, value: PropTypes.number })
  ).isRequired,
  t: PropTypes.func.isRequired,
};

const TableFoodLogPanel = ({ stat }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);

  // Merge/filter/sort/sum only recompute when the underlying stat changes —
  // the panel only mounts while its row is expanded, but re-renders happen
  // freely while it's open (toggles, unit-preference changes, etc.), so this
  // keeps those re-renders from redoing the merge/sort every time.
  const { orderedMeals, totals } = useMemo(() => {
    const hasExternalFoodLog = (stat.foodLog?.meals?.length ?? 0) > 0;
    const hasInternalFoodLog = (stat.internalFoodLog?.meals?.length ?? 0) > 0;

    const source =
      hasExternalFoodLog && hasInternalFoodLog
        ? `${stat.foodLog.source}, ${stat.internalFoodLog.source}`
        : stat.foodLog?.source || stat.internalFoodLog?.source || "";

    const mergedFoodLog = hasInternalFoodLog
      ? mergeFoodLogs(stat.foodLog, stat.internalFoodLog, source)
      : stat.foodLog;

    // Only meals with at least one logged food — a named meal with nothing
    // logged under it (e.g. "Snack" added but never filled in) has nothing
    // to show, so it's dropped rather than rendering an empty column.
    const meals = (mergedFoodLog?.meals || []).filter((meal) => (meal.foods || []).length > 0);
    const sorted = sortMeals(meals);
    return { orderedMeals: sorted, totals: sumMacros(sorted) };
  }, [stat]);

  if (orderedMeals.length === 0) return null;

  const kcalAbbr = t("common:units.kcalAbbr");
  const gramAbbr = t("common:units.gramAbbr");

  return (
    <div className="log-card log-card--food-log">
      <div className="log-card__header">
        <SvgIcon src="/img/new-icons/restaurant.svg" useCurrentColor={true} />
        {t("calendar.cards.foodLog.title")}
        <div className="np-table-detail-totals">
          <span className="np-table-detail-total">
            <span className="np-table-detail-total__badge np-table-detail-total__badge--protein">
              {t("common:macros.proteinAbbr")}
            </span>
            {Math.round(totals.proteins)}{gramAbbr}
          </span>
          <span className="np-table-detail-total">
            <span className="np-table-detail-total__badge np-table-detail-total__badge--carbs">
              {t("common:macros.carbsAbbr")}
            </span>
            {Math.round(totals.carbs)}{gramAbbr}
          </span>
          <span className="np-table-detail-total">
            <span className="np-table-detail-total__badge np-table-detail-total__badge--fat">
              {t("common:macros.fatAbbr")}
            </span>
            {Math.round(totals.fats)}{gramAbbr}
          </span>
        </div>
      </div>
      <div className="np-table-detail-food-meals">
        {orderedMeals.map((meal, index) => {
          const mealMacros = getMacros(meal);
          return (
            <div
              className="log-card__food-meal np-table-detail-food-meal"
              key={`${meal.name || meal.foodName}-${index}`}
            >
              {/* border-b + explicit solid/0-width sides (Tailwind's preflight is off, so
                  there's no default border-style/width): divider between the meal's header (name, kcal, P/C/F)
                  and its foods. pb-2 sits on the right column (the taller one,
                  it used to be .log-card__food-meal-name's mb-2 that spaced the
                  header), mb-2 spaces the divider from the first food. */}
              <div className="log-card__food-meal-header mb-2 border-x-0 border-t-0 border-b border-solid border-(--white-20)">
                <span className="log-card__food-meal-name">
                  {meal.name || meal.foodName}
                </span>
                <span className="flex flex-col items-end pb-2">
                  <span className="log-card__food-meal-kcal np-table-detail-food-meal-kcal">
                    {getMealKcal(meal)} {kcalAbbr}
                  </span>
                  {mealMacros.length > 0 && (
                    <MacroValues macros={mealMacros} t={t} />
                  )}
                </span>
              </div>
              <ul className="log-card__food-items gap-2!">
                {(meal.foods || []).map((food, foodIndex) => {
                  const kcal = getFoodKcal(food);
                  const serving = getServingLabel(food);
                  const macros = getMacros(food);
                  return (
                    <li
                      className="log-card__food-item"
                      key={`${food.name}-${foodIndex}`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="min-w-0">{food.name}</span>
                        {kcal != null && (
                          <span className="shrink-0 tabular-nums font-semibold text-(--text-primary)">
                            {kcal} {kcalAbbr}
                          </span>
                        )}
                      </div>
                      {(serving || macros.length > 0) && (
                        <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-(--white-40)">
                          <span className="min-w-0">{serving}</span>
                          {macros.length > 0 && (
                            <MacroValues macros={macros} t={t} />
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

TableFoodLogPanel.propTypes = { stat: PropTypes.object.isRequired };

export default TableFoodLogPanel;
